import { conf } from 'config';
import { LOG_SEVERITY, LogEntry } from 'store/types/LoggerTypes';
import { LoggerHttpService } from 'services/LoggerHttpService';

const getPrintableDate = (): string => {
  const d = new Date();
  let month = '' + (d.getMonth() + 1);
  let day = '' + d.getDate();
  const year = d.getFullYear();
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const seconds = d.getSeconds();

  if (month.length < 2) {
    month = '0' + month;
  }
  if (day.length < 2) {
    day = '0' + day;
  }

  return `${month}-${day}-${year} ${hours}:${minutes}:${seconds}`;
};

class Logger {
  private readonly name: string = '';

  constructor(name: string) {
    this.name = name;
  }

  public error(...messages: Array<string | number | boolean>): void {
    return this.issueMessage(messages, LOG_SEVERITY.ERROR);
  }

  public info(...messages: Array<string | number | boolean>): void {
    return this.issueMessage(messages, LOG_SEVERITY.INFO);
  }

  public debug(...messages: Array<string | number | boolean>): void {
    return this.issueMessage(messages, LOG_SEVERITY.DEBUG);
  }

  public log(...messages: Array<string | number | boolean>): void {
    return this.issueMessage(messages, LOG_SEVERITY.DEFAULT);
  }

  public warn(...messages: Array<string | number | boolean>): void {
    return this.issueMessage(messages, LOG_SEVERITY.WARNING);
  }

  public issueMessage(messages: Array<string | number | boolean>, level: LOG_SEVERITY): void {
    try {
      const printableDate = getPrintableDate();
      const log = `${level} ${this.name} ${printableDate} ${messages.join(' ')}`;

      let jsLogLevel = (level as string).toLowerCase();
      if (level === LOG_SEVERITY.WARNING) {
        jsLogLevel = 'warn';
      } else if (level === LOG_SEVERITY.DEFAULT) {
        jsLogLevel = 'log';
      }

      if (!conf.isProduction) {
        // Pipe to browser console as well as service.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (console as any)[jsLogLevel](log);
      }

      const logAttributes = {
        logger: this.name,
        dateTime: printableDate,
        timestamp: Date.now(),
        message: messages.join(' '),
      };

      const logEntry: LogEntry = {
        severity: level,
        payload: JSON.stringify(logAttributes),
      };

      if (conf.useXHRLogger) {
        LoggerHttpService.postLog(logEntry);
      }
    } catch (err) {
      try {
        if (typeof err === 'string') {
          console.error('LOGGING ERROR: Try debugging the logger service.', err);
        } else if (err instanceof Error) {
          console.error('LOGGING ERROR: Try debugging the logger service.', err.name, err.message, err.stack);
        }
      } catch (ignored) {
        console.error('LOGGING FAILURE: Check logger service.');
      }
    }
  }
}

export class LoggerService {
  public static getLogger(name: string): Logger {
    return new Logger(name);
  }
}
